/**
 * Console navigation information architecture.
 *
 * Desktop can afford a long sidebar. Mobile cannot. Work destinations live in
 * one Work group pinned under Starred, then split into LegalZoom-style job
 * subgroups (Form, Comply, Desk, Grow, Operate) so the flat A–Z list is
 * scannable. Account / Billing / Subscriptions stay as settings groups.
 * Home stays under Starred. mySubscriptions and Team stay in Account unless
 * the person stars them.
 */

export const CONSOLE_NAV_COMPACT_MAX_PX = 1080;
export const CONSOLE_NAV_COMPACT_MQ = `(max-width: ${CONSOLE_NAV_COMPACT_MAX_PX}px)`;

export type ConsoleNavGroupId = 'work' | 'subscriptions' | 'billing' | 'account';

/** Job-verb buckets inside Work — mirrors LegalZoom Business (Register / Run / Protect) + Personal. */
export type ConsoleWorkSubgroupId = 'form' | 'comply' | 'desk' | 'grow' | 'operate';

export type ConsoleNavItem = {
  id: string;
  label: string;
};

export type ConsoleNavGroup<T extends ConsoleNavItem = ConsoleNavItem> = {
  id: ConsoleNavGroupId;
  label: string;
  items: T[];
  subgroups?: ConsoleWorkSubgroup<T>[];
};

export type ConsoleWorkSubgroup<T extends ConsoleNavItem = ConsoleNavItem> = {
  id: ConsoleWorkSubgroupId;
  label: string;
  items: T[];
};

export type ConsoleNavGroupLabels = Record<ConsoleNavGroupId, string>;
export type ConsoleWorkSubgroupLabels = Record<ConsoleWorkSubgroupId, string>;

/** Top-level destinations rendered beside Home, not nested under a group. */
export const CONSOLE_NAV_PINNED_ITEM_IDS: readonly string[] = ['home'];

/** Work is always first (right under Starred). Settings groups follow A–Z by label. */
export const CONSOLE_NAV_GROUP_ORDER: readonly ConsoleNavGroupId[] = [
  'work',
  'subscriptions',
  'billing',
  'account',
];

/**
 * Stable order for Work subgroups (not A–Z — job sequence: form → comply → desk → grow → run).
 */
export const CONSOLE_WORK_SUBGROUP_ORDER: readonly ConsoleWorkSubgroupId[] = [
  'form',
  'comply',
  'desk',
  'grow',
  'operate',
];

/** Destination ids that belong in each Work subgroup. Items inside a subgroup sort A–Z by label. */
export const CONSOLE_WORK_SUBGROUP_ITEM_IDS: Record<ConsoleWorkSubgroupId, readonly string[]> = {
  // LegalZoom “Register your business” + Personal estate
  form: ['businessFormation', 'willsTrusts'],
  // LegalZoom “Run your business” compliance lane
  comply: [
    'ofac',
    'ofacCompliance',
    'ofacDashboards',
    'ofacLiveFeed',
    'ofacScreening',
    'ofacInvestigations',
    'ofacWhitelist',
    'ofacCapta',
    'websiteCompliance',
    'cloudCompliance',
    'dodContracts',
    'educationCompliance',
    'aiCompliance',
    'complianceCalendar',
  ],
  // Diligence / desk tools (Mail beside CompliDesk — same pattern as jfgoss.com/mail)
  desk: ['research', 'complidesk', 'emailInbox', 'agent'],
  // Client growth apps
  grow: ['crm', 'inlet', 'razzmatazz'],
  // Platform operate tools
  operate: [
    'postlude',
    'socials',
    'voiceMessages',
    'costOptimization',
    'admin',
    'audits',
    'keycloakAdmin',
  ],
};

/** Destination ids that belong in each group. Membership only — sidebar lists sort A–Z by label. */
export const CONSOLE_NAV_GROUP_ITEM_IDS: Record<ConsoleNavGroupId, readonly string[]> = {
  work: CONSOLE_WORK_SUBGROUP_ORDER.flatMap((id) => CONSOLE_WORK_SUBGROUP_ITEM_IDS[id]),
  subscriptions: ['subscriptionLevels', 'payingSubscriptions', 'expiredSubscriptions'],
  billing: ['payments', 'billing', 'subscriptions'],
  account: ['personal', 'security', 'privacy', 'dashboards', 'tips', 'style', 'mySubscriptions', 'team'],
};

export const DEFAULT_WORK_SUBGROUP_LABELS_EN: ConsoleWorkSubgroupLabels = {
  form: 'Form & protect',
  comply: 'Compliance',
  desk: 'Desk',
  grow: 'Grow',
  operate: 'Operate',
};

export const DEFAULT_WORK_SUBGROUP_LABELS_ES: ConsoleWorkSubgroupLabels = {
  form: 'Formar y proteger',
  comply: 'Cumplimiento',
  desk: 'Escritorio',
  grow: 'Crecer',
  operate: 'Operar',
};

const GROUP_BY_ITEM_ID: Record<string, ConsoleNavGroupId> = Object.fromEntries(
  CONSOLE_NAV_GROUP_ORDER.flatMap((groupId) =>
    CONSOLE_NAV_GROUP_ITEM_IDS[groupId].map((itemId) => [itemId, groupId]),
  ),
) as Record<string, ConsoleNavGroupId>;

const WORK_SUBGROUP_BY_ITEM_ID: Record<string, ConsoleWorkSubgroupId> = Object.fromEntries(
  CONSOLE_WORK_SUBGROUP_ORDER.flatMap((subgroupId) =>
    CONSOLE_WORK_SUBGROUP_ITEM_IDS[subgroupId].map((itemId) => [itemId, subgroupId]),
  ),
) as Record<string, ConsoleWorkSubgroupId>;

export function isPinnedConsoleNavItem(itemId: string): boolean {
  return CONSOLE_NAV_PINNED_ITEM_IDS.includes(itemId);
}

export function consoleNavGroupForItem(itemId: string): ConsoleNavGroupId | 'home' | 'pinned' {
  if (itemId === 'home') {
    return 'home';
  }
  if (isPinnedConsoleNavItem(itemId)) {
    return 'pinned';
  }
  if (itemId === 'subscriptionHub') {
    return 'subscriptions';
  }
  return GROUP_BY_ITEM_ID[itemId] || 'account';
}

export function consoleWorkSubgroupForItem(itemId: string): ConsoleWorkSubgroupId | null {
  return WORK_SUBGROUP_BY_ITEM_ID[itemId] || null;
}

/** Hard load / refresh: every job group starts collapsed, including Home. */
export function initialOpenConsoleNavGroup(_itemId?: string): ConsoleNavGroupId | null {
  return null;
}

/** Clicking a destination opens its group so siblings stay visible. Pinned items stay collapsed. */
export function openGroupForNavSelection(itemId: string): ConsoleNavGroupId | null {
  const group = consoleNavGroupForItem(itemId);
  if (group === 'home' || group === 'pinned') {
    return null;
  }
  return group;
}

/**
 * When opening Work (or landing on a Work page), open the matching job subgroup.
 * Returns null for Home / Account / Billing — do not fall back to Form & protect.
 */
export function openWorkSubgroupForNavSelection(itemId: string): ConsoleWorkSubgroupId | null {
  return consoleWorkSubgroupForItem(itemId);
}

/** Default Work subgroup when Work is opened with no page match — Compliance, not Form. */
export function defaultWorkSubgroupWhenOpeningWork(): ConsoleWorkSubgroupId {
  return 'comply';
}

function sortNavItemsAlphabetically<T extends ConsoleNavItem>(
  items: T[],
  locale: string,
): T[] {
  return [...items].sort((left, right) =>
    left.label.localeCompare(right.label, locale, { sensitivity: 'base' }),
  );
}

export function partitionWorkSubgroups<T extends ConsoleNavItem>(
  items: T[],
  labels: ConsoleWorkSubgroupLabels,
  locale = 'en',
): ConsoleWorkSubgroup<T>[] {
  const buckets: Record<ConsoleWorkSubgroupId, T[]> = {
    form: [],
    comply: [],
    desk: [],
    grow: [],
    operate: [],
  };
  items.forEach((item) => {
    const subgroupId = consoleWorkSubgroupForItem(item.id) || 'operate';
    buckets[subgroupId].push(item);
  });
  return CONSOLE_WORK_SUBGROUP_ORDER
    .map((id) => ({
      id,
      label: labels[id],
      items: sortNavItemsAlphabetically(buckets[id], locale),
    }))
    .filter((subgroup) => subgroup.items.length > 0);
}

export function groupConsoleNavItems<T extends ConsoleNavItem>(
  items: T[],
  labels: ConsoleNavGroupLabels,
  locale = 'en',
  workSubgroupLabels: ConsoleWorkSubgroupLabels = locale.startsWith('es')
    ? DEFAULT_WORK_SUBGROUP_LABELS_ES
    : DEFAULT_WORK_SUBGROUP_LABELS_EN,
): { home: T | null; pinned: T[]; groups: ConsoleNavGroup<T>[] } {
  const home = items.find((item) => item.id === 'home') || null;
  const pinned = CONSOLE_NAV_PINNED_ITEM_IDS
    .filter((id) => id !== 'home')
    .map((id) => items.find((item) => item.id === id))
    .filter((item): item is T => Boolean(item));
  const buckets: Record<ConsoleNavGroupId, T[]> = {
    work: [],
    subscriptions: [],
    billing: [],
    account: [],
  };
  items.forEach((item) => {
    if (isPinnedConsoleNavItem(item.id)) {
      return;
    }
    const groupId = consoleNavGroupForItem(item.id);
    buckets[groupId === 'home' || groupId === 'pinned' ? 'account' : groupId].push(item);
  });
  const built = CONSOLE_NAV_GROUP_ORDER
    .map((id) => {
      const groupItems = id === 'subscriptions'
        ? buckets[id]
        : sortNavItemsAlphabetically(buckets[id], locale);
      const group: ConsoleNavGroup<T> = {
        id,
        label: labels[id],
        items: groupItems,
      };
      if (id === 'work') {
        group.subgroups = partitionWorkSubgroups(groupItems, workSubgroupLabels, locale);
      }
      return group;
    })
    .filter((group) => group.items.length > 0);
  const workGroup = built.find((group) => group.id === 'work');
  const settingsGroups = built
    .filter((group) => group.id !== 'work')
    .sort((left, right) => left.label.localeCompare(right.label, locale, { sensitivity: 'base' }));
  return {
    home,
    pinned,
    groups: workGroup ? [workGroup, ...settingsGroups] : settingsGroups,
  };
}

export function navItemMatchesQuery(item: ConsoleNavItem, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return true;
  }
  return item.label.toLowerCase().includes(needle) || item.id.toLowerCase().includes(needle);
}

export function filterConsoleNavGroups<T extends ConsoleNavItem>(
  groups: ConsoleNavGroup<T>[],
  query: string,
  workSubgroupLabels?: ConsoleWorkSubgroupLabels,
  locale = 'en',
): ConsoleNavGroup<T>[] {
  const needle = query.trim();
  if (!needle) {
    return groups;
  }
  const labels = workSubgroupLabels
    || (locale.startsWith('es') ? DEFAULT_WORK_SUBGROUP_LABELS_ES : DEFAULT_WORK_SUBGROUP_LABELS_EN);
  return groups
    .map((group) => {
      const items = group.items.filter((item) => navItemMatchesQuery(item, needle));
      if (items.length === 0) {
        return null;
      }
      const next: ConsoleNavGroup<T> = { ...group, items };
      if (group.id === 'work') {
        next.subgroups = partitionWorkSubgroups(items, labels, locale);
      }
      return next;
    })
    .filter((group): group is ConsoleNavGroup<T> => Boolean(group));
}

/**
 * Accordion open state is explicit (`openGroupId`), not inferred from the
 * active page. Inferring from the active item made Work impossible to
 * collapse while viewing Access Management (or any other Work destination).
 * Clicking a destination still opens that group via `openGroupForNavSelection`.
 */
export function shouldExpandConsoleNavGroup(
  groupId: ConsoleNavGroupId,
  _activeItemId: string,
  query: string,
  openGroupId: ConsoleNavGroupId | null,
): boolean {
  if (query.trim()) {
    return true;
  }
  return openGroupId === groupId;
}

export function shouldExpandWorkSubgroup(
  subgroupId: ConsoleWorkSubgroupId,
  _activeItemId: string,
  query: string,
  openSubgroupId: ConsoleWorkSubgroupId | null,
): boolean {
  if (query.trim()) {
    return true;
  }
  return openSubgroupId === subgroupId;
}
